import React from 'react'
import { Link } from 'react-router-dom'
import { tv } from 'tailwind-variants'
import { ISidebarResource } from 'types'
import useProject from 'hooks/useProject'
import { useApplicationStore } from 'hooks/application'
import HubBadge from 'components/sidebar/hubBadge'
import Skeleton from 'components/skeleton'
import { hubHomePath } from 'components/sidebar/hubHome'
import { SIDEBAR_RESTORE_CLEARANCE, SIDEBAR_TOP_ROW_HEIGHT } from 'components/sidebar/surface'
import { breadcrumbFolder, PAGE_HEADER_ACTIONS, PAGE_HEADER_GROUP } from './styles'

export { breadcrumbFolder, pageHeaderButton, PAGE_HEADER_ACTIONS, PAGE_HEADER_GROUP } from './styles'

// The header row a hub resource's page opens with (documents, and tables to
// come): its name on the left, its actions on the right, in the manner of a
// Notion page header.
//
// It is as tall as the sidebar's top row, so with the sidebar open the two read
// as one line, and with it hidden the floating Show sidebar button sits centred
// in the header's left clearance, beside the breadcrumb. With the sidebar
// hidden the breadcrumb also names the hub and folder, since that's the context
// that goes missing with it.
const header = tv({
  slots: {
    bar: 'flex items-center gap-2 pr-4 shrink-0 bg-surface text-body select-none',
    // A container, so the crumbs can drop out when the title area gets narrow.
    // The table toolbar's title size (0.9rem).
    crumbs: '@container flex items-center gap-1.5 min-w-0 flex-1 text-[0.9rem]',
    // Shrinks long before the folder and the name do ([flex-shrink:1000]), down
    // to the badge and an ellipsis, and goes altogether below 28rem. It reads as
    // strongly as the name, since it's the context that goes missing with the
    // sidebar; the hover fill says it's a link. The -mx-1 keeps the badge on the
    // header's left edge despite the fill's padding.
    hub: 'hidden @min-[28rem]:flex items-center gap-2 min-w-0 [flex-shrink:1000] -mx-1 px-1 py-0.5 rounded text-body hover:bg-surface-sunken transition-colors',
    // The weight goes on the name, not the link: style.scss pins every a:link
    // to 400, which outranks a utility class on the link itself.
    hubName: 'truncate font-semibold',
    // Only punctuation, so the faintest text colour. Each separator goes with
    // the crumb before it.
    separator: 'hidden @min-[28rem]:inline shrink-0 text-faint',
    // The folder is closer context than the hub, so it gives way after it: it
    // shrinks before the name, and goes below 18rem.
    folder: 'hidden @min-[18rem]:block min-w-0 truncate font-semibold [flex-shrink:100]',
    folderSeparator: 'hidden @min-[18rem]:inline shrink-0 text-faint',
    title: 'min-w-0 truncate font-semibold'
  }
})

const { bar, crumbs, hub, hubName, separator, folder, folderSeparator, title } = header()

interface PageHeaderProps {
  /** The resource's name, the breadcrumb's last crumb. */
  name: string
  /** The hub resource the page shows, whose sidebar folder the breadcrumb names. */
  resource?: Pick<ISidebarResource, 'kind' | 'publicId'>
  /** For a page outside a hub, what it belongs to, always named before it: "Account" / Settings & Security. */
  parent?: string
  /** Prefix for the header's test ids: `${testIdPrefix}-header`, `-header-hub`, `-header-folder`. */
  testIdPrefix: string
  /** A test id for the name itself, where existing tests already look for one. */
  titleTestId?: string
  /** Shown in place of the name while it's being renamed. */
  renameInput?: React.ReactNode
  /** Double-clicking the name starts renaming it. */
  onRename?: () => void
  /** While the resource loads, a placeholder stands in for its name. */
  loading?: boolean
  /** The actions, and anything the header renders alongside them (menus, modals). */
  children?: React.ReactNode
}

const PageHeader: React.FC<PageHeaderProps> = ({
  name,
  resource,
  parent,
  testIdPrefix,
  titleTestId,
  renameInput,
  onRename,
  loading,
  children
}) => {
  const { project } = useProject()
  const { sidebarWidth } = useApplicationStore()
  const sidebarHidden = sidebarWidth === 0
  const folderName = breadcrumbFolder(project.hubFolders, project.sidebarResources, resource)

  return (
    <header
      className={bar()}
      data-testid={`${testIdPrefix}-header`}
      style={{
        height: SIDEBAR_TOP_ROW_HEIGHT,
        paddingLeft: sidebarHidden ? SIDEBAR_RESTORE_CLEARANCE : 16
      }}
    >
      <nav className={crumbs()} aria-label="Breadcrumb">
        {/* A page outside a hub names what it belongs to instead, never the hub last opened. */}
        {sidebarHidden && !parent && project && project.name && (
          <>
            <Link to={hubHomePath(project)} className={hub()} data-testid={`${testIdPrefix}-header-hub`}>
              <HubBadge name={project.name} colour={project.primaryColour} textColour={project.fontColour} />
              <span className={hubName()}>{project.name}</span>
            </Link>
            <span className={separator()} aria-hidden>
              /
            </span>
          </>
        )}
        {sidebarHidden && !parent && folderName && (
          <>
            <span className={folder()} data-testid={`${testIdPrefix}-header-folder`}>
              {folderName}
            </span>
            <span className={folderSeparator()} aria-hidden>
              /
            </span>
          </>
        )}
        {parent && (
          <>
            <span className={folder()} data-testid={`${testIdPrefix}-header-parent`}>
              {parent}
            </span>
            <span className={folderSeparator()} aria-hidden>
              /
            </span>
          </>
        )}
        {loading && <Skeleton className="block h-4 w-[180px] min-w-0" />}
        {!loading && renameInput}
        {!loading && !renameInput && (
          <div className={title()} aria-current="page" data-testid={titleTestId} onDoubleClick={onRename}>
            {name}
          </div>
        )}
      </nav>
      {children}
    </header>
  )
}

export const PageHeaderActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={PAGE_HEADER_ACTIONS}>{children}</div>
)

/** One group of related actions, announced by its label. */
export const PageHeaderGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className={PAGE_HEADER_GROUP} role="group" aria-label={label}>
    {children}
  </div>
)

export default PageHeader
